import { nanoid } from "nanoid"
import { MAX_MESSAGE_LENGTH } from "../../constants"
import type {
  AttachmentRef,
  ChatMessage,
  ChatRefusal,
  EditMessage,
  EventMessage,
  Message,
  MessageStatus,
  RetractionMessage,
  SharedItemRef,
} from "../../types"
import { isRoomJid, jidLocalPart, type Jid } from "../../xmpp/jid"
import {
  conversationPeer,
  isOwnMessage,
  messageHasId,
  sameMessage,
} from "../../xmpp/stanzas/message"
import {
  isRetractionRefusal,
  type MessageBounce,
} from "../../xmpp/stanzas/error"
import type { ChatSession, ReadyChatSession } from "../session"
import { reportChatError } from "../../reportChatError"
import type { ChatState, ChatStore } from "../store"
import { canNotify, isNotifiable } from "../notifications"
import { stopTyping } from "./typing"
import { IS_APP } from "../../../vuplex/constants"
import { conversationFor, isActive, messageIn, threadFor } from "../selectors"
import {
  archiveConversation,
  markConversationRead,
  nameUnseenRoom,
  previewEdit,
  previewRetraction,
  renameConversation,
  upsertConversation,
  dropRemovedGroup,
} from "./conversations"

const MAX_PENDING_NOTIFICATIONS = 50

// ─── What the UI calls ──────────────────────────────────────────────────────

export async function sendMessage(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  body: string,
  attachment?: AttachmentRef
) {
  if (!session.isReady()) return
  if (body.length > MAX_MESSAGE_LENGTH) return

  const message: ChatMessage = {
    type: "chat",
    id: nanoid(),
    author: jidLocalPart(session.selfJid),
    body,
    timestamp: Date.now(),
    routing: { from: session.selfJid, to: peer },
    retracted: false,
    attachment,
    status: "sending",
  }

  // On screen before the socket takes it, so a send that is slow or fails is
  // visible as such rather than a message that simply never appeared.
  upsertMessage(store, peer, message, false)
  upsertConversation(store, message, peer)

  await deliver(session, store, peer, message)
}

/**
 * Try a "failed" message again, from its own retry affordance. Reuses the id:
 * a failed send never reached the server, so nothing there collides with it,
 * and the row stays put rather than jumping to the bottom of the thread.
 */
export async function resendMessage(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  messageId: string
) {
  if (!session.isReady()) return

  const message = messageIn(store.getState(), peer, messageId)
  if (message?.type !== "chat" || message.status !== "failed") return

  setMessageStatus(store, peer, messageId, "sending")
  await deliver(session, store, peer, message)
}

/**
 * Hand a message to the socket and record how it went. A transport failure —
 * the send never left — is not the same as a server refusal (see `onBounce`):
 * the message stays put as "failed" for the reader to retry, rather than being
 * dropped as a bounce is.
 */
async function deliver(
  session: ReadyChatSession,
  store: ChatStore,
  peer: Jid,
  message: ChatMessage
) {
  try {
    await session.client.sendMessage(peer, message)
    setMessageStatus(store, peer, message.id, "sent")
  } catch (error) {
    setMessageStatus(store, peer, message.id, "failed")
    reportChatError(`failed to send message to ${peer}`, error)
  }
}

/**
 * Share a learning item (FAME-237). The `<share>` reference goes on the wire
 * with a deep-link `body` fallback; the optimistic echo carries the reference,
 * which the receiver — and our own thread — resolve to a card.
 */
export async function sendShare(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  ref: SharedItemRef,
  body: string
) {
  if (!session.isReady()) return

  const id = await session.client.sendShare(peer, ref, body)

  const message: ChatMessage = {
    type: "chat",
    id,
    author: jidLocalPart(session.selfJid),
    body,
    timestamp: Date.now(),
    routing: { from: session.selfJid, to: peer },
    retracted: false,
    share: ref,
  }

  upsertMessage(store, peer, message, false)
  upsertConversation(store, message, peer)
}

export async function retractMessage(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  mId: string
) {
  if (!session.isReady()) return

  const targetId = canonicalId(store.getState(), peer, mId)
  if (!targetId) return

  // The delete never left the socket — distinct from the server refusing it,
  // which arrives later as a bounce. Either way the reader hears the same "could
  // not be deleted"; here nothing was applied, so there is nothing to revert.
  try {
    await session.client.retract(peer, targetId, mId)
  } catch (error) {
    reportChatError(`failed to retract message ${mId}`, error)
    raiseRefusal(store, peer, "retract")
    return
  }

  applyRetraction(store, peer, mId, true)
}

/**
 * Change the text of one of our messages. It shows at once; the stanza bounces
 * on an id of its own, so a refusal puts back exactly what this replaced.
 */
export async function editMessage(
  session: ChatSession,
  store: ChatStore,
  peer: Jid,
  mId: string,
  body: string
) {
  if (!session.isReady()) return
  if (!body || body.length > MAX_MESSAGE_LENGTH) return

  const message = messageIn(store.getState(), peer, mId)
  if (message?.type !== "chat" || message.retracted) return
  if (message.body === body) return

  // The composer has already closed, so a silent return would drop the text.
  const targetId = canonicalId(store.getState(), peer, mId)
  if (!targetId) return raiseRefusal(store, peer, "edit")

  const bounceId = nanoid()
  try {
    await session.client.edit(peer, targetId, body, bounceId)
  } catch (error) {
    reportChatError(`failed to edit message ${mId}`, error)
    raiseRefusal(store, peer, "edit")
    return
  }

  applyEdit(store, peer, {
    ...message,
    body,
    editedAt: Date.now(),
    pendingEdit: {
      bounceId,
      body: message.body,
      editedAt: message.editedAt,
    },
  })
}

/**
 * Fill a thread in from the archive. Live send/receive works without history,
 * so a failure here just means the thread shows less than it could.
 */
export async function loadHistory(
  session: ChatSession,
  store: ChatStore,
  peer: Jid
) {
  if (!session.isReady()) return

  try {
    const { messages } = await session.client.loadHistory(peer)
    for (const message of messages) upsertMessage(store, peer, message, false)
    // Only on success: a failed load leaves the thread loading, and the
    // reconnect backfill tries again.
    store.setState(({ historyLoaded }) => ({
      historyLoaded: { ...historyLoaded, [peer]: true },
    }))
  } catch (error) {
    reportChatError(`failed to load history for ${peer}`, error)
  }
}

// ─── What the client calls ──────────────────────────────────────────────────

export function onMessage(
  session: ChatSession,
  store: ChatStore,
  message: Message
) {
  if (!session.isReady()) return

  const peer = conversationPeer(message, session.selfJid)

  // Checked before upsertConversation mints the row: a live message from a room
  // the store does not know means we were added to a group mid-session.
  const unseenRoom = isRoomJid(peer) && !conversationFor(store.getState(), peer)

  // A redelivered stanza must not notify twice.
  const redelivered = threadFor(store.getState(), peer).some((m) =>
    sameMessage(m, message)
  )

  upsertMessage(store, peer, message, false)

  const incoming = !isOwnMessage(message, session.selfJid)
  if (incoming && message.author) stopTyping(store, peer, message.author)
  // Viewing means its thread is open AND this tab has focus. `document.hidden`
  // is not enough — it stays false when the reader switches to another window or
  // app — so a hidden tab, another window, or another conversation all count as
  // away: the message stays unread and earns a notification (FAME-858).
  // The app's webview never notifies, so there it keeps the thread-open rule.
  const viewing =
    isActive(store.getState(), peer) && (IS_APP || document.hasFocus())
  // Outbound (undefined) leaves the flag alone, and so do system notices: they
  // are not messages, and an archived group's row may be hidden from the
  // sidebar — it must not light a badge the user cannot reach.
  const unread = incoming && message.type !== "event" ? !viewing : undefined

  upsertConversation(store, message, peer, unread)

  if (message.type === "event")
    applyEvent(store, peer, message, session.selfJid)

  // The minted row has no name; the group endpoint does. Fetch it rather than
  // showing `g-<uuid>` until the next boot.
  if (unseenRoom) nameUnseenRoom(session, store, peer)

  // Read only once actually viewed — stored locally, and stuck on reload and
  // across devices.
  if (incoming && viewing) markConversationRead(session, store, peer)

  // Away from an unmuted conversation: hand the UI an OS notification to fire.
  if (
    !redelivered &&
    isNotifiable(message, incoming) &&
    !viewing &&
    !conversationFor(store.getState(), peer)?.muted &&
    canNotify()
  ) {
    store.setState((state) => ({
      // Capped well above a burst the notifier is still naming: nothing drains
      // the queue while it is not mounted (error or loading screens).
      pendingNotifications: [
        ...state.pendingNotifications,
        {
          id: message.id,
          peer,
          author: message.author ?? "",
          body: message.body ?? "",
        },
      ].slice(-MAX_PENDING_NOTIFICATIONS),
    }))
  }
}

export function onRetraction(
  _session: ChatSession,
  store: ChatStore,
  { routing, retractedMessageId }: RetractionMessage
) {
  applyRetraction(store, routing.from, retractedMessageId, true)
}

/**
 * Someone's Edit, ours from another tab or the room's echo of one from here
 * included. The server's text and time settle the message. One not in the
 * thread is dropped: the archive already holds the new text for when it loads.
 */
export function onEdit(
  session: ChatSession,
  store: ChatStore,
  edit: EditMessage
) {
  if (!session.isReady()) return

  const peer = conversationPeer(edit, session.selfJid)
  const message = messageIn(store.getState(), peer, edit.editedMessageId)
  if (message?.type !== "chat" || message.retracted) return

  applyEdit(store, peer, {
    ...withoutEdit(message),
    body: edit.body,
    ...(edit.editedAt !== undefined && { editedAt: edit.editedAt }),
  })
}

export function onBounce(
  _session: ChatSession,
  store: ChatStore,
  { peer, messageId, condition, refusal }: MessageBounce
) {
  // First: an Edit's refusal reasons overlap a retraction's, and only an Edit
  // bounces on an id no message has.
  const edited = awaitingEdit(store, peer, messageId)
  if (edited) return refuseEdit(store, peer, edited, refusal)

  if (refusal === "already-retracted") return // no problem

  // Removed from the group, which a removed member is never told live. Their
  // send or deletion is undone, and they hear why.
  if (refusal === "not-member") {
    const message = messageIn(store.getState(), peer, messageId)
    if (message?.type === "chat" && message.retracted)
      applyRetraction(store, peer, messageId, false)
    else if (messageId) removeMessage(store, peer, messageId)
    return raiseRefusal(store, peer, "not-member")
  }

  // A refused retraction. The stub comes back down, and the reader is told —
  // otherwise a message they deleted quietly reappears.
  if (refusal && isRetractionRefusal(refusal)) {
    applyRetraction(store, peer, messageId, false)
    return raiseRefusal(
      store,
      peer,
      refusal === "expired" ? "retract-expired" : "retract"
    )
  }

  // A send that raced the archive freeze. The refusal may be the first we hear
  // of the freeze, so the row learns it here. An unmarked `not-acceptable` from
  // a room is read the same way.
  if (
    refusal === "archived" ||
    (condition === "not-acceptable" && isRoomJid(peer))
  ) {
    archiveConversation(store, peer, true)
    if (messageId) removeMessage(store, peer, messageId)
    return raiseRefusal(store, peer, "archived-group")
  }

  // Anything else the server refused — the oversize cap, a policy violation.
  // The echo was never archived, so leaving it in the thread would show a
  // message that does not exist anywhere but here.
  if (messageId) removeMessage(store, peer, messageId)
  raiseRefusal(store, peer, "send")
}

// ─── Writers and shared logic ───────────────────────────────────────────────

export function upsertMessage(
  store: ChatStore,
  peer: Jid,
  message: Message,
  overwrite: boolean
) {
  const messagesByPeer = store.getState().messagesByPeer
  const messages = messagesByPeer[peer] ?? []
  const existing = messages.find((m) => sameMessage(m, message))

  const settled =
    existing && !overwrite ? backfillStanzaId(existing, message) : message
  if (!settled) return

  // Replacing, not appending: a retraction reuses its target's id, so adding it
  // would leave the original sitting beside the stub.
  const next = existing
    ? messages.map((m) => (m === existing ? settled : m))
    : [...messages, settled].sort((a, b) => a.timestamp - b.timestamp)

  store.setState({ messagesByPeer: { ...messagesByPeer, [peer]: next } })
}

/** Move one of our sends between sending → sent → failed, leaving it in place. */
function setMessageStatus(
  store: ChatStore,
  peer: Jid,
  id: string,
  status: MessageStatus
) {
  const message = messageIn(store.getState(), peer, id)
  if (message?.type !== "chat") return

  upsertMessage(store, peer, { ...message, status }, true)
}

function backfillStanzaId(
  existing: Message,
  incoming: Message
): Message | undefined {
  if (existing.roomStanzaId || !incoming.roomStanzaId) return undefined

  return { ...existing, roomStanzaId: incoming.roomStanzaId }
}

/** The id the server knows a message by: its origin-id in a 1:1, the room's stanza-id in a group. */
function canonicalId(
  state: ChatState,
  peer: Jid,
  mId: string
): string | undefined {
  if (!isRoomJid(peer)) return mId

  const stanzaId = messageIn(state, peer, mId)?.roomStanzaId
  if (!stanzaId)
    console.warn(`[chat] no room stanza-id for ${mId} yet, not sending`)

  return stanzaId
}

/**
 * Drop a message from its thread — for a bounced send, whose optimistic echo
 * would otherwise sit there looking delivered. It was never archived
 * server-side, so a reload would drop it anyway.
 */
export function removeMessage(store: ChatStore, peer: Jid, messageId: string) {
  const messagesByPeer = store.getState().messagesByPeer
  const messages = messagesByPeer[peer]
  if (!messages) return

  store.setState({
    messagesByPeer: {
      ...messagesByPeer,
      [peer]: messages.filter((m) => !messageHasId(m, messageId)),
    },
  })
}

/**
 * Hand the UI something to say. Overwrites rather than queues: two refusals in
 * a row are two toasts, and the reader only needs the last one if they arrive
 * together.
 */
function raiseRefusal(store: ChatStore, peer: Jid, kind: ChatRefusal["kind"]) {
  store.setState({ refusal: { kind, peer } })
}

/** Said once. The UI calls this when it has. */
export function clearRefusal(store: ChatStore) {
  store.setState({ refusal: undefined })
}

/** Drop one entry. `MessageNotifier` calls this once it has fired, or given up on, it. */
export function clearNotification(store: ChatStore, peer: Jid, id: string) {
  store.setState((state) => ({
    pendingNotifications: state.pendingNotifications.filter(
      (pending) => pending.peer !== peer || pending.id !== id
    ),
  }))
}

/**
 * Flag a message rather than replace it, so `body` survives and reverting a
 * refused retraction is just clearing the flag.
 */
export function applyRetraction(
  store: ChatStore,
  peer: Jid,
  messageId: string | undefined,
  retracted: boolean
) {
  const message = messageIn(store.getState(), peer, messageId)
  if (message?.type !== "chat") return

  const updated = { ...message, retracted }
  upsertMessage(store, peer, updated, true)
  previewRetraction(store, peer, updated)
}

function applyEdit(store: ChatStore, peer: Jid, edited: ChatMessage) {
  upsertMessage(store, peer, edited, true)
  previewEdit(store, peer, edited)
}

/** One of our messages whose Edit has not been settled, by the id that Edit bounces on. */
function awaitingEdit(
  store: ChatStore,
  peer: Jid,
  bounceId: string | undefined
): ChatMessage | undefined {
  if (!bounceId) return undefined

  return threadFor(store.getState(), peer).find(
    (m): m is ChatMessage =>
      m.type === "chat" && m.pendingEdit?.bounceId === bounceId
  )
}

/** The text comes back, whatever the reason; the reader hears the reason. */
function refuseEdit(
  store: ChatStore,
  peer: Jid,
  message: ChatMessage,
  refusal: MessageBounce["refusal"]
) {
  const { pendingEdit } = message
  applyEdit(store, peer, {
    ...withoutEdit(message),
    body: pendingEdit?.body,
    ...(pendingEdit?.editedAt !== undefined && {
      editedAt: pendingEdit.editedAt,
    }),
  })

  // The server holds it deleted: show it so, with nothing to apologise for.
  if (refusal === "already-retracted")
    return applyRetraction(store, peer, message.id, true)
  if (refusal === "not-member") return raiseRefusal(store, peer, "not-member")
  if (refusal === "archived") {
    archiveConversation(store, peer, true)
    return raiseRefusal(store, peer, "archived-group")
  }
  raiseRefusal(store, peer, refusal === "expired" ? "edit-expired" : "edit")
}

function withoutEdit({
  editedAt: _editedAt,
  pendingEdit: _pendingEdit,
  ...message
}: ChatMessage): ChatMessage {
  return message
}

/**
 * Which row change a notice means. Other membership and admin changes touch
 * nothing here — those lists live in the group details and are refetched on
 * open. Our own removal reaches us just before we are unsubscribed.
 */
/**
 * A replayed notice older than the row: the row has moved on since (we were
 * added back), so the removal no longer holds.
 */
function isStale(store: ChatStore, peer: Jid, event: EventMessage): boolean {
  const row = conversationFor(store.getState(), peer)
  return !!row && row.lastTimestamp > event.timestamp
}

function applyEvent(
  store: ChatStore,
  peer: Jid,
  event: EventMessage,
  self: Jid
) {
  switch (event.event) {
    case "member_removed":
      if (event.member === jidLocalPart(self) && !isStale(store, peer, event))
        dropRemovedGroup(store, peer)
      return
    case "archived":
      return archiveConversation(store, peer, true)
    case "unarchived":
      return archiveConversation(store, peer, false)
    case "renamed":
      return renameConversation(store, peer, event.name)
  }
}
